import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import { AlertTriangle, Filter as FilterIcon } from 'lucide-react';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import FilterDrawer from '../../../shared/FilterDrawer';
import ColumnPicker from '../../../shared/ColumnPicker';
import useTabColumns from '../../../shared/useTabColumns';
import CancelButton from '../../../shared/CancelButton';
import { CategoryPill } from '../../../shared/Pill';
import { Pagination, PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize } from '../../../shared/pagination';
import { formatPrice } from '../Prices/priceLookupShared';
import ProgressDot from '../../../shared/ProgressDot';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { formatDate } from '../../../shared/tenantClock';
import DateInput from '../../../shared/DateInput';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { SALES_ORDER_STATUS_LABELS as STATUS_LABELS } from '../../../shared/salesOrderStatus';
import { formatMoney, formatNumber } from '../../../shared/numberFormat';
import loadCustomerOptions from '../shared/loadCustomerOptions';
import TaxBasisLine from '../../../shared/TaxBasisLine';
import { PaymentStatusPill, balanceText } from '../shared/DocumentPayments';
import OriginFilterFields, { useDocumentCreators } from '../shared/OriginFilterFields';
import './SalesOrderStepper.css';

const FILTER_SCREEN_KEY = 'sales-orders';
const DEFAULT_FILTERS = { status: 'all', backorder: false, awaiting_portal: false, date_from: '', date_to: '', customers: [], payment_term_id: '', invoiced: '', origin: '', created_by: '' };
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };
// Optional list-table columns (Columns picker, persisted per-user via
// /api/column-preferences) — Date/Order #/Customer/Picked/Packed/Shipped/
// Total (inc GST, freight included) stay always-visible (SYSTEM_COLUMNS)
// since they're identity/fulfilment-progress columns, not display
// preference. Total ex GST starts off — available for whoever needs it.
const OPTIONAL_COLUMNS = [
  { key: 'po_reference', label: 'PO Reference' },
  { key: 'source', label: 'Source' },
  { key: 'status', label: 'Status' },
  // Unpaid / Part-paid / Paid / No charge for a Prepay Customer's order (issue #338).
  { key: 'payment', label: 'Payment' },
  // The Customer's Payment Term, its own or its Tier's.
  { key: 'payment_terms', label: 'Payment Terms' },
  // Has an Invoice that isn't void, so its shipping charge is fixed.
  { key: 'invoiced', label: 'Invoiced' },
  { key: 'amount_ex_gst', label: 'Total ex GST' },
  { key: 'origin', label: 'Origin' },
  { key: 'created_by', label: 'Created by' },
];
const SYSTEM_COLUMNS = ['Date', 'Order #', 'Customer', 'Picked', 'Packed', 'Shipped', 'Total'];
const DEFAULT_VISIBLE_COLUMNS = ['po_reference', 'source', 'status', 'payment', 'payment_terms', 'invoiced'];

// Derived from lines_qty_total/lines_qty_picked_total (see
// salesOrderController.listSalesOrders) — none/partial/full, same "some
// lines vs all lines" rule as the Order-Level Status in
// vis_order_fulfilment.md. Shared by both the Picked and Packed columns —
// same shape, just a different qty field.
function progressIcon(total, done, verb) {
  if (total === 0 || done === 0) return { state: 'empty', title: `Not ${verb}` };
  if (done >= total) return { state: 'full', title: `Fully ${verb}` };
  return { state: 'partial', title: `Partially ${verb}` };
}

// How long a portal order has waited since its latest submit (issue #276).
function waitingFor(submittedAt) {
  if (!submittedAt) return '—';
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(submittedAt).getTime()) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.floor(hours / 24)} days`;
}

function SalesOrdersTable() {
  const { canCreate } = useScreenAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('order_date');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  // Drill-down from SalesDashboard.js's metric tiles (e.g. Backorders) hands
  // its starting filter through router state — same "already-loaded record
  // through router state" shape QuotePage.js's Customer preload uses,
  // rather than a round-trip lookup. Ignored once the user changes filters.
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS, ...(location.state?.initialFilters || {}) }));
  const { status, backorder, awaiting_portal: awaitingPortal, date_from: dateFrom, date_to: dateTo, payment_term_id: paymentTermId, invoiced, origin, created_by: createdBy } = filters;
  const [paymentTerms, setPaymentTerms] = useState([]);
  useEffect(() => {
    axios.get('/api/payment-terms', { params: { status: 'all' } }).then((res) => setPaymentTerms(res.data.data || [])).catch(() => setPaymentTerms([]));
  }, []);
  const creators = useDocumentCreators('/api/sales-orders/creators');
  const [awaitingCount, setAwaitingCount] = useState(0);
  // Picked as { value, label } so a saved tab can show the names back.
  const customerIds = (filters.customers || []).map((c) => c.value).join(',');
  // Free-text, unbounded — order #, Customer or PO reference (the
  // backend's `search`), not a SavedFilterTabs dimension.
  const [searchText, setSearchText] = useState('');
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const tabColumns = useTabColumns(OPTIONAL_COLUMNS, SYSTEM_COLUMNS);
  const showPoReference = visibleColumns.includes('po_reference');
  const showSource = visibleColumns.includes('source');
  const showStatus = visibleColumns.includes('status');
  const showPayment = visibleColumns.includes('payment');
  const showPaymentTerms = visibleColumns.includes('payment_terms');
  const showInvoiced = visibleColumns.includes('invoiced');
  const showAmountEx = visibleColumns.includes('amount_ex_gst');
  const showOrigin = visibleColumns.includes('origin');
  const showCreatedBy = visibleColumns.includes('created_by');
  const applyFilters = (values) => { setFilters(values); setPage(1); };
  const [filterDraft, setFilterDraft] = useState(null);
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };

  useDefaultPageSize(setLimit);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/sales-orders', { params: { status, backorder: backorder ? 'true' : undefined, awaiting_portal: awaitingPortal ? 'true' : undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined, customer_ids: customerIds || undefined, payment_term_id: paymentTermId || undefined, invoiced: invoiced || undefined, origin: origin || undefined, created_by: createdBy || undefined, search: searchText || undefined, sortBy, sortOrder, page, limit } });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
      setSummary(res.data.summary || null);
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
      setSummary(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    axios.get('/api/sales-orders/awaiting-portal-count')
      .then((res) => setAwaitingCount(res.data.data?.count || 0))
      .catch(() => setAwaitingCount(0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, backorder, awaitingPortal, dateFrom, dateTo, customerIds, paymentTermId, invoiced, origin, createdBy, searchText, sortBy, sortOrder, page, limit]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(col);
      setSortOrder('ASC');
    }
  };

  const renderDrawerFields = (draft, setDraftField) => (
    <div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Status</label><br />
        <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="all">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>
      <div style={{ marginTop: 14 }}>
        <label style={FIELD_LABEL_STYLE}>Customers</label><br />
        <AsyncSelect
          isMulti
          cacheOptions
          defaultOptions
          loadOptions={loadCustomerOptions}
          value={draft.customers || []}
          onChange={(picked) => setDraftField('customers', picked || [])}
          placeholder="All customers"
          noOptionsMessage={() => 'No matching customers'}
          loadingMessage={() => 'Searching...'}
          styles={{ control: (base) => ({ ...base, fontSize: 14 }), option: (base) => ({ ...base, fontSize: 14 }) }}
        />
      </div>
      <div style={{ marginTop: 14 }}>
        <label style={FIELD_LABEL_STYLE}>Payment Terms</label><br />
        <select className="styled-input" value={draft.payment_term_id || ''} onChange={(e) => setDraftField('payment_term_id', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All</option>
          {paymentTerms.map((t) => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
        </select>
      </div>
      <div style={{ marginTop: 14 }}>
        <label style={FIELD_LABEL_STYLE}>Invoiced</label><br />
        <select className="styled-input" value={draft.invoiced || ''} onChange={(e) => setDraftField('invoiced', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All</option>
          <option value="yes">Invoiced</option>
          <option value="no">Not invoiced</option>
        </select>
      </div>
      <div style={{ marginTop: 14 }}>
        <label style={FIELD_LABEL_STYLE}>Order date from</label><br />
        <DateInput className="styled-input" value={draft.date_from || ''} onChange={(e) => setDraftField('date_from', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
      <div style={{ marginTop: 14 }}>
        <label style={FIELD_LABEL_STYLE}>Order date to</label><br />
        <DateInput className="styled-input" value={draft.date_to || ''} onChange={(e) => setDraftField('date_to', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
      <div style={{ marginTop: 14 }}>
        <label style={{ ...FIELD_LABEL_STYLE, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!draft.backorder} onChange={(e) => setDraftField('backorder', e.target.checked)} />
          Backorders only
        </label>
      </div>
      <div style={{ marginTop: 14 }}>
        <label style={{ ...FIELD_LABEL_STYLE, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!draft.awaiting_portal} onChange={(e) => setDraftField('awaiting_portal', e.target.checked)} />
          Awaiting acceptance (portal)
        </label>
      </div>
      <OriginFilterFields draft={draft} setDraftField={setDraftField} creators={creators} />
    </div>
  );

  const visibleColCount = 7 + (showPoReference ? 1 : 0) + (showSource ? 1 : 0) + (showStatus ? 1 : 0) + (showPayment ? 1 : 0) + (showPaymentTerms ? 1 : 0) + (showInvoiced ? 1 : 0) + (showOrigin ? 1 : 0) + (showCreatedBy ? 1 : 0) + (awaitingPortal ? 1 : 0) + (showAmountEx ? 1 : 0);

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Sales Orders</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={() => navigate('/sales-orders/new')}
              style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: 'pointer' }}
            >
              New Sales Order
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <input
            className="styled-input"
            placeholder="Order #, Customer or PO ref..."
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            style={{ width: 230 }}
          />
          {searchText && (
            <button type="button" onClick={() => { setSearchText(''); setPage(1); }} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
          )}
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
        <ColumnPicker
          screenKey={FILTER_SCREEN_KEY}
          columns={OPTIONAL_COLUMNS}
          defaultVisible={DEFAULT_VISIBLE_COLUMNS}
          onChange={setVisibleColumns}
          tabColumns={tabColumns}
          systemColumns={SYSTEM_COLUMNS}
        />
        {awaitingCount > 0 && !awaitingPortal && (
          <button
            type="button"
            onClick={() => applyFilters({ ...DEFAULT_FILTERS, awaiting_portal: true })}
            title="Show Submitted portal orders, oldest first"
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #f0c36d', background: '#fff8e1', color: '#7a5200', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
          >
            {awaitingCount} awaiting acceptance (portal)
          </button>
        )}
      </div>

      <div style={{ margin: '10px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          tabColumns={tabColumns}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the Sales Order list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      {/* Reminder: this total takes off backordered qty that was later
          cancelled (to match the Sales by Period chart), but each row's
          Total is the full order value with freight — so the Total ex GST
          column won't add up to this figure. */}
      {activeFilterCount > 0 && summary && (
        <div style={{ margin: '14px 24px 0', fontSize: 12, color: '#666', display: 'flex', gap: 16, alignItems: 'baseline' }}>
          <span><strong style={{ fontSize: 15, color: 'var(--text-dark)' }}>{formatMoney(summary.value)}</strong> sales ex GST</span>
          <span><strong style={{ fontSize: 15, color: 'var(--text-dark)' }}>{formatNumber(summary.orders, { decimals: 0 })}</strong> orders</span>
        </div>
      )}

      <div style={{ margin: '18px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '11%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '21%' }} />
            {showPoReference && <col style={{ width: '12%' }} />}
            {showSource && <col style={{ width: '12%' }} />}
            {/* Room for the blocker marker, the Status pill and a Hold tag. */}
            {showStatus && <col style={{ width: '15%' }} />}
            {showPayment && <col style={{ width: '9%' }} />}
            {showPaymentTerms && <col style={{ width: '9%' }} />}
            {showInvoiced && <col style={{ width: '8%' }} />}
            {showOrigin && <col style={{ width: '10%' }} />}
            {showCreatedBy && <col style={{ width: '12%' }} />}
            {awaitingPortal && <col style={{ width: '9%' }} />}
            <col style={{ width: '7%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '7%' }} />
            {showAmountEx && <col style={{ width: '12%' }} />}
            <col style={{ width: '13%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={() => handleSort('order_date')}>
                Date {sortBy === 'order_date' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
              </th>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={() => handleSort('number')}>
                Order # {sortBy === 'number' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
              </th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Customer</th>
              {showPoReference && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>PO Reference</th>}
              {showSource && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Source</th>}
              {showStatus && <th style={{ padding: '12px 8px 12px 30px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Status</th>}
              {showPayment && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Payment</th>}
              {showPaymentTerms && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Payment Terms</th>}
              {showInvoiced && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Invoiced</th>}
              {showOrigin && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Origin</th>}
              {showCreatedBy && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Created by</th>}
              {awaitingPortal && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'right' }}>Waiting</th>}
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Picked</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Packed</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Shipped</th>
              {showAmountEx && (
                <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'right' }}>
                  Total
                  <TaxBasisLine>ex GST</TaxBasisLine>
                </th>
              )}
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'right' }}>
                Total
                <TaxBasisLine>inc GST</TaxBasisLine>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={visibleColCount} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={visibleColCount} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => {
              const total = Number(item.lines_qty_total || 0);
              const pick = progressIcon(total, Number(item.lines_qty_picked_total || 0), 'picked');
              const pack = progressIcon(total, Number(item.lines_qty_packed_total || 0), 'packed');
              const ship = progressIcon(total, Number(item.lines_qty_shipped_total || 0), 'shipped');
              return (
                <tr
                  key={item.id}
                  className="list-row"
                  onClick={() => navigate(`/sales-orders/${item.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ padding: '6px 8px', fontSize: 13, color: '#666' }}>{formatDate(item.order_date)}</td>
                  <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.display_number}</td>
                  <td style={{ padding: '6px 8px', fontSize: 14 }}>{item.customer?.name}</td>
                  {showPoReference && <td style={{ padding: '6px 8px', fontSize: 13, color: '#666' }}>{item.po_reference || '—'}</td>}
                  {showSource && <td style={{ padding: '6px 8px', fontSize: 13, color: '#666' }}>{item.source_quote?.display_number || 'Direct'}</td>}
                  {showStatus && (
                    <td style={{ padding: '6px 8px', textAlign: 'left', fontSize: 13, whiteSpace: 'nowrap' }}>
                      {/* What stops a Draft or Submitted order being Accepted
                          (issue #268), in a fixed slot before the pill so every
                          pill and marker lines up; Status itself stays the
                          order's stage. */}
                      <span title={item.accept_blockers?.length ? `Can't be Accepted: ${item.accept_blockers.join('; ')}` : undefined} style={{ display: 'inline-flex', width: 18, marginRight: 4, verticalAlign: -2, cursor: item.accept_blockers?.length ? 'help' : 'default' }}>
                        {item.accept_blockers?.length > 0 && <AlertTriangle size={14} strokeWidth={2.5} style={{ color: '#b91c1c' }} />}
                      </span>
                      <CategoryPill value={STATUS_LABELS[item.status] || item.status} />
                      {/* On Hold sits on top of the Status, as on the order's
                          own stepper (SalesOrderStepper.js). */}
                      {item.on_hold && <span className="so-stepper-tag hold" title={item.on_hold_reason ? `On Hold: ${item.on_hold_reason}` : 'On Hold'} style={{ marginLeft: 6 }}>Hold</span>}
                    </td>
                  )}
                  {showPayment && <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}><PaymentStatusPill status={item.payment_status} title={item.payment_status ? balanceText(item.balance_owing) : undefined} /></td>}
                  {showPaymentTerms && <td style={{ padding: '6px 8px', fontSize: 13 }}>{item.payment_term?.name || '—'}</td>}
                  {showInvoiced && <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>{item.invoiced ? <CategoryPill value="Invoiced" /> : '—'}</td>}
                  {showOrigin && <td style={{ padding: '6px 8px', fontSize: 13, color: '#666' }}>{item.origin_label || '—'}</td>}
                  {showCreatedBy && <td style={{ padding: '6px 8px', fontSize: 13, color: '#666' }}>{item.created_by_name || '—'}</td>}
                  {awaitingPortal && <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 13, color: '#7a5200' }} title={item.submitted_at ? `Submitted ${new Date(item.submitted_at).toLocaleString()}` : undefined}>{waitingFor(item.submitted_at)}</td>}
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}><ProgressDot {...pick} /></td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}><ProgressDot {...pack} /></td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}><ProgressDot {...ship} /></td>
                  {showAmountEx && <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 14 }}>{formatPrice(item.total_ex_gst)}</td>}
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 14 }}>{formatPrice(item.total_inc_gst)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Sales Orders"
        rightContent={(
          <select className="styled-input" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}>
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default SalesOrdersTable;
